import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const DATE_FORMAT = "MM/DD/YYYY";

// "Edit Date" already carries a data-action/data-track pair for GA tracking (initializers.js.erb's
// doTrack), so its click is bound through a target in connect() rather than a second data-action.
export default class extends Controller {
  static get targets() { return ["calendarToggle", "sendMessageButton", "copyBanner"]; }

  // The calendar drawer (subdomain/gifts/_calendar_drawer) is a document-wide sibling of this
  // mount, not a descendant, so it can't be a Stimulus target -- kept as a plain element reference
  // instead, native everywhere except the daterangepicker plugin itself.
  connect() {
    this.drawer = document.querySelector(".calendar-drawer");
    this.dateInputEl = this.drawer.querySelector(".send-date-calendar-date-range-picker");
    this.$dateInput = $(this.dateInputEl);
    this.date = moment().format(DATE_FORMAT);

    this.$dateInput.daterangepicker({
      opens: "left",
      alwaysShowCalendars: true,
      showCustomRangeLabel: false,
      singleDatePicker: true,
      parentEl: ".send-date-drawer-datepicker",
      autoApply: true,
      autoUpdateInput: false,
      startDate: this.date,
      minDate: new Date()
    });

    this.dateInputEl.click();
    this.dateInputEl.value = this.date;

    this.onApplyDate = (event, picker) => {
      this.date = picker.startDate.format(DATE_FORMAT);
      this.dateInputEl.value = this.date;
    };
    this.onChangeDate = (event) => { this.date = event.target.value; };
    this.onCloseClick = () => this.closeDrawer();
    this.onSubmitClick = () => this.calendarSubmitForm();
    this.onThumbClick = this.onThumbClick.bind(this);
    this.onCalendarToggle = (event) => {
      document.querySelectorAll(".actions-notice.error").forEach(el => el.remove());
      // Per-target attribute (the triggering row's own data-url) -- not a Values API candidate,
      // same shape as decisions.md D3.
      this.pendingUrl = event.target.dataset.url;
      this.openDrawer();
    };

    // querySelectorAll, not querySelector: the drawer renders TWO [data-toggle='close']
    // elements -- the header's X icon and the footer's Close button -- and the jQuery `.find()`
    // this replaced bound both. Binding only the first leaves the footer Close button dead.
    this.closeButtons = Array.from(this.drawer.querySelectorAll("[data-toggle='close']"));
    this.submitButtons = Array.from(this.drawer.querySelectorAll("[data-toggle='submit']"));

    this.$dateInput.on("apply.daterangepicker.activityGifts", this.onApplyDate);
    this.$dateInput.on("change.activityGifts", this.onChangeDate);
    this.closeButtons.forEach((button) => button.addEventListener("click", this.onCloseClick));
    this.submitButtons.forEach((button) => button.addEventListener("click", this.onSubmitClick));
    this.calendarToggleTargets.forEach(toggle => toggle.addEventListener("click", this.onCalendarToggle));
    this.element.addEventListener("click", this.onThumbClick);
  }

  disconnect() {
    this.$dateInput.off("apply.daterangepicker.activityGifts", this.onApplyDate);
    this.$dateInput.off("change.activityGifts", this.onChangeDate);
    this.closeButtons.forEach((button) => button.removeEventListener("click", this.onCloseClick));
    this.submitButtons.forEach((button) => button.removeEventListener("click", this.onSubmitClick));
    this.calendarToggleTargets.forEach(toggle => toggle.removeEventListener("click", this.onCalendarToggle));
    this.element.removeEventListener("click", this.onThumbClick);
  }

  openDrawer() {
    this.drawer.parentElement.classList.add("active");
    setTimeout(() => { this.drawer.classList.remove("closed"); }, 5);
  }

  closeDrawer() {
    this.drawer.parentElement.classList.remove("active");
    this.drawer.classList.add("closed");
  }

  calendarSubmitForm() {
    const url = this.pendingUrl;
    const params = { gift: { scheduled_at: this.date } };

    document.querySelectorAll(".actions-notice.error").forEach(el => el.remove());

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*"
    });
    options.body = JSON.stringify(params);

    fetch(url, options).
      then(response => {
        if (response.ok) {
          location.reload();
          return;
        }

        // Unreachable through the UI today (nothing in the model raises a validation failure
        // reachable from "Edit Date" -- see the spec's header comment), so left on jQuery's
        // .after(htmlString) rather than rewritten to build DOM nodes with no spec able to prove
        // the rewrite: message is a canned ActiveModel error string, not user-echoed input.
        response.json().then(data => {
          if (response.status === 422) {
            data.scheduled_at.forEach(message => $(".actions-notice").after(this.errorMessage(message)));
          } else {
            this.errorMessage("An error occurred");
          }
        });
      }).
      catch(this.logFailedCall);
  }

  errorMessage(message) {
    return `<p class='actions-notice error'>
        <i class='icon-lasso-warning'></i>
        ${message}
      </p>`;
  }

  logFailedCall(response) { console.warn(response); }

  // Toggles the real `disabled` attribute rather than relying on native HTML5 `required`, which
  // would leave the button clickable.
  toggleSendMessageButton(event) {
    if (!this.hasSendMessageButtonTarget) { return; }

    this.sendMessageButtonTarget.disabled = event.target.value.trim().length === 0;
  }

  copyLink(event) {
    event.target.select();
    document.execCommand("copy");
    this.copyBannerTarget.style.display = "flex";

    // fadeOut is jQuery's own animation utility, not plain DOM work -- no native equivalent
    // without reimplementing the opacity transition and its timing.
    setTimeout(() => { $(this.copyBannerTarget).fadeOut(); }, 2000);
  }

  // The .thumbs/#product-detail-modal markup lives in gives/collections/products/_modal_body,
  // rendered here but shared with collection_generator/products and subdomain/collections/products
  // (other clusters' controllers) -- left as a delegated listener over querySelector/querySelectorAll
  // rather than Targets/Classes API/data-action, none of which this cluster can add to that partial
  // without leaking an activity-gifts-only attribute into markup other controllers also render.
  onThumbClick(event) {
    const link = event.target.closest(".thumbs a");
    if (!link) { return; }
    event.preventDefault();

    this.element.querySelectorAll(".thumbs a").forEach(a => a.classList.remove("active"));
    event.target.parentElement.classList.add("active");

    const src = event.target.getAttribute("src").split("tile")[0];
    let offsetAttr = "offsetTop";
    let scrollAttr = "scrollTop";
    let containerSelector = "#product-detail-modal .product-details-desktop .fullsize";
    if (window.innerWidth < 1130) {
      offsetAttr = "offsetLeft";
      scrollAttr = "scrollLeft";
      containerSelector = "#product-detail-modal .product-details-mobile .fullsize";
    }
    const container = this.element.querySelector(containerSelector);
    const img = Array.from(container.querySelectorAll(`img[src^="${src}"]`))[0];
    if (img) {
      container[scrollAttr] = img[offsetAttr] - 16; // 16 is the margin
    } else {
      container[scrollAttr] = 0;
    }
  }
}
